import { ref, computed, readonly, watch, ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useChartStore } from '@/stores/chartStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useActionResultMessages } from '@/composables/useActionResultMessages';
import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
import { useSymbolInformation } from '@/composables/useSymbolInformation';
import { useModals } from '@/composables/useModals';
import { getChart } from '@/composables/getChart';
import { updateChart } from '@/composables/updateChart';

let isInitialized = false;
const isSavingChartComments = ref(false);
const temporaryChartComments = ref('');

let chartBeingViewedComments: ComputedRef<string> | undefined;

async function saveChartComments(comments: string) {
  const chartStore = useChartStore();
  const { isLoggedIn } = useAuthentication();
  const {
    addGreenActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { isSymbolBeingViewedNotFound, isSymbolBeingViewedAnEODSymbolOnAnIntradayChart } = useSymbolInformation();
  const { isChartBeingViewedReadonly } = useWorkbenchSelections();
  const { launchCreateAccountModal, launchSaveChartAsModal } = useModals();

  if (
    isSavingChartComments.value ||
    isSymbolBeingViewedNotFound.value ||
    isSymbolBeingViewedAnEODSymbolOnAnIntradayChart.value
  ) {
    return;
  }

  const trimmedComments = comments.trim();

  if (trimmedComments.length > 2048) {
    addRedActionResultMessage('Chart comments must be between 0 and 2048 characters.');
    return;
  }

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  isSavingChartComments.value = true;
  temporaryChartComments.value = trimmedComments;

  const chart = await getChart(chartStore.recidOfChartBeingViewed);

  if (!chart || isChartBeingViewedReadonly.value) {
    isSavingChartComments.value = false;

    launchSaveChartAsModal();
    return;
  }

  clearTemporaryChartComments();

  const { recid } = chart;

  try {
    await updateChart(recid, { comments: trimmedComments });

    addGreenActionResultMessage('Success! Your comments have been saved!');
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to save comments for chart with recid: ${recid} and comments: ${trimmedComments}.`, e);

    addRedActionResultMessage('Failed to save comments. Please try again.');
  }

  await getChart(recid, true);

  isSavingChartComments.value = false;
}

function clearTemporaryChartComments() {
  temporaryChartComments.value = '';
}

export function useChartComments() {
  const chartStore = useChartStore();
  const { chartBeingViewed } = useWorkbenchSelections();

  const { recidOfChartBeingViewed } = storeToRefs(chartStore);

  if (!isInitialized) {
    watch(recidOfChartBeingViewed, clearTemporaryChartComments);

    isInitialized = true;
  }

  chartBeingViewedComments = chartBeingViewedComments ?? computed(() => chartBeingViewed.value?.comments ?? '');

  return {
    isSavingChartComments: readonly(isSavingChartComments),
    chartBeingViewedComments,
    clearTemporaryChartComments,
    saveChartComments,
    temporaryChartComments: readonly(temporaryChartComments)
  };
}
